import React from "react";
import { Box, Typography, IconButton, Tooltip, Chip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BlockIcon from "@mui/icons-material/Block";
import RestoreIcon from "@mui/icons-material/Restore";
import StatusChip from "../common/StatusChip";

const PcpConfigDataGrid = ({ rows, loading, onEdit, onDeactivate, onReactivate }) => {
  const columns = [
    {
      field: "pcpFullName",
      headerName: "PCP Name",
      flex: 1.2,
      minWidth: 180,
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {row.pcpFullName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.title || "—"} | {row.jobPositionName}
          </Typography>
        </Box>
      ),
    },
    {
      field: "medicalCenter",
      headerName: "Location",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "patientsAssigned",
      headerName: "Patients",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => value ?? 0,
    },
    {
      field: "maxPatient",
      headerName: "Capacity",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => value ?? "No limit",
    },
    {
      field: "capacityPercentage",
      headerName: "Usage",
      width: 80,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) =>
        value != null ? `${Math.round(value)}%` : "—",
    },
    {
      field: "ageRange",
      headerName: "Age Range",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => value || "Any",
    },
    {
      field: "flags",
      headerName: "Accepts",
      flex: 0.8,
      minWidth: 160,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {row.highRisk && <Chip label="High Risk" size="small" color="warning" />}
          {row.highCost && <Chip label="High Cost" size="small" color="info" />}
          {row.medicare && <Chip label="Medicare" size="small" color="primary" />}
          {row.critical && <Chip label="Critical" size="small" color="error" />}
        </Box>
      ),
    },
    {
      field: "daysWork",
      headerName: "Days/Wk",
      width: 80,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => value ?? "—",
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: ({ value }) => <StatusChip active={!!value} />,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Box>
          {!!row.status ? (
            <>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => onEdit(row)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Deactivate">
                <IconButton size="small" color="error" onClick={() => onDeactivate(row)}>
                  <BlockIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Reactivate">
              <IconButton size="small" color="success" onClick={() => onReactivate(row)}>
                <RestoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      getRowId={(row) => `${row.employeePcpId}-${row.locationId}`}
      loading={loading}
      autoHeight
      pageSizeOptions={[25, 50, 100]}
      initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
      disableRowSelectionOnClick
      density="comfortable"
      sx={{ border: "none" }}
    />
  );
};

export default PcpConfigDataGrid;
